import React, { useRef } from 'react';
import usePresence from '../../use/usePresence';
import Aviso from '../ui/Aviso';
import ProgressBar from '../ui/ProgressBar';
import { X, UploadSimple, CheckCircle, CircleNotch, Bug } from 'phosphor-react';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  mediaPreview: string | null;
  mediaType: string | null;
  handleMediaChange: React.ChangeEventHandler<HTMLInputElement>;
  removeMedia: () => void;
  loading: boolean;
  /** 0..100 enquanto a mídia sobe; null quando não há upload em curso. */
  progressoMidia: number | null;
  error: string;
  success: boolean;
  handleSubmit: React.FormEventHandler;
}

export default function BugReportModal({
  isOpen,
  onClose,
  title,
  setTitle,
  description,
  setDescription,
  mediaPreview,
  mediaType,
  handleMediaChange,
  removeMedia,
  loading,
  progressoMidia,
  error,
  success,
  handleSubmit,
}: BugReportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Shell proprio (nao usa o ActionModal), entao a presenca e o par de animacoes
  // precisam ser repetidos aqui.
  const { montado, estado } = usePresence(isOpen, 200);

  if (!montado) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 data-[state=closed]:pointer-events-none"
    >
      <div
        data-state={estado}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        data-state={estado}
        className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-soft-lg dark:shadow-none dark:border dark:border-gray-700 overflow-hidden data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out sm:data-[state=open]:animate-scale-in sm:data-[state=closed]:animate-scale-out"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blush-50 dark:bg-blush-900/20 rounded-xl flex items-center justify-center">
              <Bug className="text-blush-500 dark:text-blush-400" size={16} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Reportar Bug
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-nude-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-nude-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-2">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <CheckCircle className="w-12 h-12 text-sage-500" />
              <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
                Bug enviado com sucesso!
              </p>
              <p className="text-sm text-nude-400 dark:text-gray-500 text-center">
                Obrigado por nos ajudar a melhorar o AutoCare.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Título
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Botão não responde ao toque"
                  maxLength={120}
                  className="w-full px-4 py-3 rounded-xl bg-nude-50 dark:bg-gray-700 border border-nude-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100 placeholder-nude-300 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blush-300 dark:focus:ring-blush-700 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Descrição
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descreva o que aconteceu, como reproduzir o problema..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-nude-50 dark:bg-gray-700 border border-nude-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100 placeholder-nude-300 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blush-300 dark:focus:ring-blush-700 transition-colors resize-none"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Foto ou Vídeo <span className="font-normal text-nude-300 dark:text-gray-500">(opcional)</span>
                </label>

                {mediaPreview ? (
                  <div className="relative rounded-xl overflow-hidden bg-nude-50 dark:bg-gray-700 border border-nude-200 dark:border-gray-600">
                    {mediaType === 'IMAGE' ? (
                      <img
                        src={mediaPreview}
                        alt="Preview"
                        className="w-full max-h-40 object-cover"
                      />
                    ) : (
                      <video
                        src={mediaPreview}
                        controls
                        className="w-full max-h-40"
                      />
                    )}
                    <button
                      type="button"
                      onClick={removeMedia}
                      className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
                      aria-label="Remover mídia"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed border-nude-200 dark:border-gray-600 bg-nude-50 dark:bg-gray-700/50 text-nude-400 dark:text-gray-500 hover:border-blush-300 dark:hover:border-blush-700 hover:text-blush-500 dark:hover:text-blush-400 transition-colors text-sm font-medium"
                  >
                    <UploadSimple className="w-4 h-4" />
                    Adicionar imagem ou vídeo
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaChange}
                  className="hidden"
                />
              </div>

              {/* 100% é o fim do envio daqui; o repasse ao storage ainda vem depois. */}
              {progressoMidia !== null && (
                <div className="flex items-center gap-2 animate-enter">
                  <ProgressBar valor={progressoMidia} className="h-1" label="Envio da mídia" />
                  <span className="text-[11px] text-nude-400 dark:text-gray-500 tabular-nums w-16 text-right">
                    {progressoMidia < 100 ? `${progressoMidia}%` : 'Enviando…'}
                  </span>
                </div>
              )}

              <Aviso mensagem={error} />

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-blush-500 hover:bg-blush-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-button transition-colors duration-300 flex items-center justify-center gap-2 active:scale-[0.97] disabled:active:scale-100"
              >
                {loading ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Bug size={16} />
                    Enviar Relatório
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
